import React from "react";
import { useApp } from "@/context/AppContext";
import { Users, Baby, BedDouble, Minus, Plus } from "lucide-react";

const Counter = ({ label, Icon, value, min, max, onChange, testid }) => (
  <div className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-2.5">
    <span className="flex items-center gap-2 text-sm font-semibold text-slate-600"><Icon size={16} className="text-tigris" /> {label}</span>
    <div className="flex items-center gap-2">
      <button type="button" data-testid={`${testid}-minus`} disabled={value <= min} onClick={() => onChange(value - 1)} className="w-7 h-7 rounded-full border border-slate-200 flex items-center justify-center disabled:opacity-30 hover:bg-white"><Minus size={13} /></button>
      <span className="w-6 text-center font-bold text-slate-800" data-testid={testid}>{value}</span>
      <button type="button" data-testid={`${testid}-plus`} disabled={value >= max} onClick={() => onChange(value + 1)} className="w-7 h-7 rounded-full border border-slate-200 flex items-center justify-center disabled:opacity-30 hover:bg-white"><Plus size={13} /></button>
    </div>
  </div>
);

// value: { check_in, check_out, rooms, adults, children_ages: [] }
export const HotelGuestPicker = ({ value, onChange, showDates = true }) => {
  const { t } = useApp();
  const g = t.guests;
  const set = (patch) => onChange({ ...value, ...patch });
  const inputCls = "w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50/60 focus:border-tigris outline-none text-sm";
  const setChildren = (n) => {
    const ages = [...value.children_ages];
    while (ages.length < n) ages.push(5);
    set({ children_ages: ages.slice(0, n) });
  };
  const nights = value.check_in && value.check_out ? Math.max(0, Math.round((new Date(value.check_out) - new Date(value.check_in)) / 86400000)) : 0;

  return (
    <div className="space-y-3" data-testid="hotel-guest-picker">
      {showDates && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t.search.checkin}</label>
            <input data-testid="hotel-checkin" type="date" className={inputCls} value={value.check_in} onChange={(e) => set({ check_in: e.target.value })} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t.search.checkout} {nights > 0 && <span className="text-tigris">· {nights} {g.nights}</span>}</label>
            <input data-testid="hotel-checkout" type="date" className={inputCls} min={value.check_in || undefined} value={value.check_out} onChange={(e) => set({ check_out: e.target.value })} required />
          </div>
        </div>
      )}
      <div className="grid sm:grid-cols-3 gap-2">
        <Counter label={g.rooms} Icon={BedDouble} value={value.rooms} min={1} max={5} testid="guest-rooms" onChange={(v) => set({ rooms: v, adults: Math.min(value.adults, v * 3), children_ages: value.children_ages.slice(0, v * 2) })} />
        <Counter label={g.adults} Icon={Users} value={value.adults} min={1} max={value.rooms * 3} testid="guest-adults" onChange={(v) => set({ adults: v })} />
        <Counter label={g.children} Icon={Baby} value={value.children_ages.length} min={0} max={value.rooms * 2} testid="guest-children" onChange={setChildren} />
      </div>
      {value.children_ages.length > 0 && (
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">{g.childAges}</label>
          <div className="flex flex-wrap gap-2">
            {value.children_ages.map((age, i) => (
              <select key={i} data-testid={`child-age-${i}`} className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white" value={age}
                onChange={(e) => { const ages = [...value.children_ages]; ages[i] = Number(e.target.value); set({ children_ages: ages }); }}>
                {Array.from({ length: 18 }, (_, a) => <option key={a} value={a}>{a === 0 ? g.underOne : `${a} ${g.years}`}</option>)}
              </select>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-1.5">{g.policy}</p>
        </div>
      )}
    </div>
  );
};

export const defaultHotelGuests = (o = {}) => ({ check_in: o.check_in || "", check_out: o.check_out || "", rooms: Number(o.rooms) || 1, adults: Number(o.adults) || 2, children_ages: o.children_ages || [] });
